import { createContext, useContext, useCallback, useState, type ReactNode } from 'react';
import { ToastContainer, type ToastData, type ToastType } from './Toast';

interface ToastContextValue {
  /** Show a toast notification */
  toast: (options: Omit<ToastData, 'id'>) => void;
  /** Show a success toast */
  success: (title: string, description?: string) => void;
  /** Show an error toast */
  error: (title: string, description?: string) => void;
  /** Show a warning toast */
  warning: (title: string, description?: string) => void;
  /** Show an info toast */
  info: (title: string, description?: string) => void;
  /** Dismiss a specific toast */
  dismiss: (id: string) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastCounter = 0;

/**
 * Toast context provider.
 * Wrap your app with this to enable toast notifications.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback((options: Omit<ToastData, 'id'>) => {
    const id = `toast-${++toastCounter}`;
    setToasts((prev) => [...prev, { ...options, id }]);
  }, []);

  const createTypeToast = useCallback(
    (type: ToastType) => (title: string, description?: string) => {
      toast({ type, title, description });
    },
    [toast]
  );

  const value: ToastContextValue = {
    toast,
    success: createTypeToast('success'),
    error: createTypeToast('error'),
    warning: createTypeToast('warning'),
    info: createTypeToast('info'),
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast functions.
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
